import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getCurrentLanguage } from '../utils/translations';
import api from '../components/api';
import { FullPageLoader } from '../components/Loader';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';

const FullDescriptionPage = () => {
  const { slug } = useParams();
  const [slide, setSlide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  // Fetch slide data
  const fetchSlide = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/hero-slides/slug/${slug}/`);
      setSlide(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching slide:', err);
      setError(err.response?.data?.error || 'Slide not found');
      setSlide(null);
    } finally {
      setLoading(false);
    }
  };

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(getCurrentLanguage());
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  // Fetch data on mount
  useEffect(() => {
    fetchSlide();
  }, [slug]);

  if (loading) {
    return (
      <>
        <Navbar />
        <FullPageLoader />
        <Footer />
      </>
    );
  }

  // Error state
  if (error || !slide) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <div className="h-16 w-16 text-red-500 mx-auto mb-4">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{currentLanguage === 'fr' ? 'Slide non trouvé' : 'Slide not found'}</h2>
            <p className="text-gray-600">{currentLanguage === 'fr' ? 'Le slide que vous recherchez n\'existe pas ou a été supprimé.' : 'The slide you are looking for does not exist or has been removed.'}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Dynamic content rendering
  const translatedTitle = currentLanguage === 'en' && slide.title_en ? slide.title_en : slide.title_fr || slide.title_en;
  const translatedDescription = currentLanguage === 'en' && slide.description_en ? slide.description_en : slide.description_fr || slide.description_en;

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-12 bg-gradient-to-br from-[#089556] via-[#07D1EB] to-[#0ba8c7]">
        <div className="absolute inset-0 bg-black/40" />
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">
              {translatedTitle}
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          {slide.image_url && (
            <div className="mb-6">
              <img
                src={slide.image_url}
                alt={translatedTitle}
                className="w-full h-64 md:h-80 object-cover rounded-xl shadow-lg"
              />
            </div>
          )}
          
          <div 
            dangerouslySetInnerHTML={{ __html: translatedDescription }}
            className="text-gray-700 leading-relaxed"
          />
        </div>
      </section>

      <Footer />
    </>
  );
};

export default FullDescriptionPage;
